import React, { Fragment, useState, useCallback, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import {
  Box,
  Flex,
  Icon,
  Text,
  Button,
  PopupMenu,
  StretchingAsset,
  TextEditor,
  utils,
  AnyObject,
  SaveDataBuilders,
  useThemeColor,
} from "@thenounproject/lingo-core";

import { CanvasComponentProps, CanvasEditableProps } from "./sharedProps";
import { ItemPolling } from "../GalleryAssetContent/PollingWrappers";
import { getVersionedItemId } from "@src/helpers/items";
import { insertData, InsertType } from "@constants/InsertType";
import useModals, { ModalTypes } from "@actions/useModals";
import useReplaceAssetFile from "@actions/assets/useReplaceAssetFile";
import useCreateItemAsset from "@actions/items/useCreateItemAsset";
import { getCanvasLayoutProperties } from "./utils";
import { SelectionWrapper } from "./SelectionWrapper";
import { CanvasItemWrapper } from "./CanvasItemWrapper";
import getAssetBackground from "@src/helpers/getAssetBackground";
import useReplaceAssetFileFromSource from "@actions/assets/useReplaceAssetFileFromSource";
import useCreateAssetsFromSource, {
  ExternalSource,
} from "@actions/items/useCreateAssetsFromSource";

// Styled components
const Wrapper = styled(Box)`
  border-radius: 4px;
  ${utils.showOnHoverCSS("show-on-hover")}
`;

const RowClear = styled(Box).attrs({
  height: 0,
  flex: "1 1 100%",
})``;

const StyledStretchingImage = styled(StretchingAsset)`
  pointer-events: none;
`;

const UploadImageButton = styled(Button).attrs(props => ({
  buttonStyle: "tertiary",
  text: "＋ Add Image",
  fontStyle: "ui.regularBold",
  theme: {
    ...props.theme,
    primaryColor: "grayDark",
    primaryColorDark: "grayDarker",
  },
  width: "100%",
}))`
  &:hover {
    text-decoration: none;
  }
`;

const Rule = styled.hr<{ color: string }>`
  border: none;
  border-top: 3px solid ${props => utils.getColor(props.color, "grayDarker")};
  ${utils.positioningCSS({ m: "none", mb: "s" })}
  transition: border-color 0.2s ease;
`;

const TextWrapper = styled(Flex).attrs({ pb: 1 })<{ canEdit: boolean; isEditing: boolean }>`
  ${props => props.canEdit && !props.isEditing && `cursor: pointer;`}
`;

const DropDownButton = styled(Flex).attrs({
  justifyContent: "space-between",
  alignItems: "center",
  height: 28,
})`
  cursor: pointer;
`;

const OverflowButton = styled(Button).attrs({
  size: "small",
  buttonStyle: "tertiary",
  icon: "navigation.overflow",
  themeOverrides: {
    primaryColor: "grayDarkest",
    primaryColorDark: "black",
  },
})`
  margin: 0 8px;
`;

const ContentWrapper = styled(Flex).attrs({
  borderRadius: "default",
})``;

// Helpers
const getFlexDirection = (alignment: string, size: number) => {
  if (!alignment) return "column";
  if (size === 1 || alignment === "top") return "column";
  return alignment === "leading" ? "row" : "row-reverse";
};

const getDisplayColor = (accentColor?: string) => {
  if (!accentColor || ["green", "red"].includes(accentColor)) {
    return utils.getColor(accentColor || "green");
  }
  return accentColor;
};

type Props = {
  guidePosition: "left" | "right" | "full";
} & CanvasComponentProps &
  CanvasEditableProps;

const CanvasGuide: React.FC<Props> = ({
  item,
  isSelected,
  onClick,
  onDoubleClick: _onDoubleClick,
  isEditing,
  onBeginEditing,
  onFinishEditing,
  onDragEnd,
  onDragStart,
  draggable,
  onOverflowSelected,
  canEdit,
  canGenerateText,
  index,
  guidePosition,
  containerWidth,
  ...rest
}) => {
  const versionedId = getVersionedItemId(item);
  const { outerWidth, size, isSquare } = getCanvasLayoutProperties({
    item,
    containerWidth,
  });
  const hasDefaultValue = item.data.content === insertData[InsertType.guide].data.content;
  const { imageAlignment, cardBackgroundColor } = item.displayProperties || {};
  const hasBgColor = cardBackgroundColor && cardBackgroundColor !== "clear";
  const flexDirection = getFlexDirection(imageAlignment, size);
  const thumbnailWidth = isSquare ? "100%" : `${100 / size}%`;
  const gap = isSquare ? undefined : "8px";
  const theme = useTheme();

  // Item properties
  const {
    asset,
    displayProperties: { displayStyle } = {},
    data: { color: accentColor, title },
  } = item;
  const displayColor = getDisplayColor(accentColor);

  const [titleDropdownOpen, setTitleDropdownOpen] = useState(false);

  // File upload handling
  const { showModal } = useModals();

  const handleAIGenerate = useCallback(() => {
    showModal(ModalTypes.GENERATE_TEXT_CONTENT, {
      item,
      buildSaveData: SaveDataBuilders.noteContent,
    });
  }, [showModal, item]);
  const [replaceAssetFile] = useReplaceAssetFile();
  const [createItemAsset] = useCreateItemAsset();
  const [replaceAssetFileFromSource] = useReplaceAssetFileFromSource();
  const [createAssetFromSource] = useCreateAssetsFromSource();

  const previewBackgroundProps = useMemo(() => {
    return getAssetBackground(item.asset, theme);
  }, [item.asset, theme]);

  const onFileSelected = useCallback(
    (files: File[]) => {
      const file = files[0];
      return item.asset
        ? replaceAssetFile({ inspectable: { item, asset: item.asset }, file })
        : createItemAsset({ item, file });
    },
    [item, replaceAssetFile, createItemAsset]
  );
  const onSourceSelected = useCallback(
    (sources: ExternalSource[]) => {
      return item.asset
        ? replaceAssetFileFromSource({
            inspectable: { item, asset: item.asset },
            source: sources[0],
          })
        : createAssetFromSource({
            sources,
            insertPosition: { kitId: item.kitId, spaceId: item.spaceId },
            item,
          });
    },
    [item, replaceAssetFileFromSource, createAssetFromSource]
  );

  const replaceFileAsset = useCallback(() => {
    showModal(ModalTypes.PICK_FILE, {
      onUploadFiles: onFileSelected,
      onImportFiles: onSourceSelected,
      prompt: "Choose image",
      inspectable: { item, asset: item.asset },
    });
  }, [item, onSourceSelected, onFileSelected, showModal]);

  // Event handlers
  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      onClick(item, e);
    },
    [onClick, item]
  );

  const handleOverflowClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOverflowSelected(item);
    },
    [item, onOverflowSelected]
  );

  const handleTitleClick = useCallback(() => setTitleDropdownOpen(true), []);
  const handleCloseTitleDropdown = useCallback(() => setTitleDropdownOpen(false), []);

  const handleChangeTitle = useCallback(
    (newTitle: string, newColor: string) => {
      if (!onFinishEditing || newTitle === title) return;
      onFinishEditing(item, { data: { title: newTitle, color: newColor } });
    },
    [onFinishEditing, item, title]
  );

  // Render helpers
  const renderImage = () => {
    if (displayStyle !== "image") return null;

    if (asset) {
      return (
        <Box width={thumbnailWidth} flexShrink={0}>
          <StyledStretchingImage
            asset={asset}
            maxHeight={600}
            {...previewBackgroundProps}
            borderRadius="4px 4px 0 0"
          />
        </Box>
      );
    }

    if (canEdit) {
      return (
        <Flex
          background="grayLightest"
          textAlign="center"
          p="m"
          width={thumbnailWidth}
          flexShrink={0}>
          <UploadImageButton onClick={replaceFileAsset} />
        </Flex>
      );
    }

    return null;
  };

  const renderTitleDropdown = () => (
    <Fragment>
      <DropDownButton
        data-testid="guide-title"
        mx="xs"
        onClick={handleTitleClick}
        data-popup-source={`guide-title-select-${item.id}`}>
        <Text pr="xs" color={displayColor} transition="color 0.2s" font="ui.regularBold">
          {title}
        </Text>
        <Icon
          iconId="action.expand"
          className="show-on-hover"
          fill={displayColor}
          transition="fill 0.2s"
          size="16"
        />
      </DropDownButton>
      {titleDropdownOpen && (
        <PopupMenu
          source={`guide-title-select-${item.id}`}
          close={handleCloseTitleDropdown}
          vPos="alignTop"
          hPos="alignLeft">
          {[
            ["DO", "green"],
            ["DON'T", "red"],
          ].map(([command, commandColor]) => (
            <PopupMenu.Item
              key={command}
              data-testid={`guide-title-${command}`}
              checked={title === command}
              title={command}
              onClick={() => handleChangeTitle(command, commandColor)}
            />
          ))}
        </PopupMenu>
      )}
    </Fragment>
  );

  const renderOverflowButton = () => (
    <OverflowButton
      className="show-on-hover"
      dataPopupSource={`overflow-menu-${item.id}`}
      onClick={handleOverflowClick}
    />
  );

  const handleFinishEditing = useCallback(
    (data: AnyObject | null) => {
      onFinishEditing(item, data);
    },
    [item, onFinishEditing]
  );

  const renderTextEditor = () => (
    <TextEditor
      itemId={item.id}
      content={item.data?.content}
      buildSaveData={SaveDataBuilders.noteContent}
      onFinishEditing={canEdit ? handleFinishEditing : undefined}
      hasDefaultValue={hasDefaultValue}
      canEdit={canEdit}
      selected={canEdit ? isSelected : undefined}
      isEditing={canEdit ? isEditing : undefined}
      onBeginEditing={canEdit ? onBeginEditing : undefined}
      aria-label="Update guide text"
      backgroundColor={hasBgColor ? cardBackgroundColor : undefined}
      onAIGenerate={canGenerateText ? handleAIGenerate : undefined}
    />
  );

  const titleElement = canEdit ? (
    renderTitleDropdown()
  ) : (
    <Text data-testid="guide-title" color={displayColor} font="ui.regularBold" mx="xs">
      {title}
    </Text>
  );

  const overflowElement = canEdit ? (
    <Flex className="show-on-hover" alignItems="center">
      {renderOverflowButton()}
    </Flex>
  ) : (
    renderOverflowButton()
  );

  const textElement = canEdit ? (
    <TextWrapper canEdit={canEdit} isEditing={isEditing}>
      {renderTextEditor()}
    </TextWrapper>
  ) : (
    <Box data-testid="guide-text">{renderTextEditor()}</Box>
  );
  const backgroundColor = useThemeColor(cardBackgroundColor);

  return (
    <CanvasItemWrapper
      data-full-width={outerWidth === "100%" || size === 1}
      outerWidth={outerWidth}
      size={size}
      index={index}
      itemId={item.id}
      {...rest}>
      <Box
        id={item.id}
        data-itemid={versionedId}
        data-dragid={versionedId}
        data-shortid={item.shortId}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}>
        <ItemPolling item={item}>
          <SelectionWrapper selected={isSelected}>
            <Fragment>
              {guidePosition === "left" && <RowClear />}
              <Wrapper
                data-selected={isSelected}
                background={hasBgColor ? backgroundColor : undefined}
                p={hasBgColor ? "s" : undefined}
                data-testid={`guide-${canEdit ? "editable" : "static"}-wrapper`}>
                <ContentWrapper flexDirection={flexDirection} onClick={handleClick} gap={gap}>
                  {renderImage()}
                  <Flex flexDirection="column" flex="1" minWidth={0}>
                    <Rule color={displayColor} data-testid="guide-hr" />
                    <Flex justifyContent="space-between" mb="s" mx={canEdit ? undefined : "xs"}>
                      {titleElement}
                      {overflowElement}
                    </Flex>
                    {textElement}
                  </Flex>
                </ContentWrapper>
              </Wrapper>
            </Fragment>
          </SelectionWrapper>
        </ItemPolling>
      </Box>
    </CanvasItemWrapper>
  );
};

export default CanvasGuide;
